import { useEffect, useState, type ReactElement } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { SourceControlProvider } from '@/components/integrations/SourceControlIntegrationCard';
import {
  useScopeChangeAction,
  useUnsavedChangeActions,
} from '@/hooks/useScopeChangeHooks';

interface ManualConnectDialogProps {
  provider: SourceControlProvider;
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (installationId: string) => void;
  isLoading?: boolean;
  error?: string;
  /** When true, the connect action is disabled. */
  readOnly?: boolean;
}

export function ManualConnectDialog({
  provider,
  organizationId,
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  error,
  readOnly = false,
}: ManualConnectDialogProps): ReactElement {
  const [installationId, setInstallationId] = useState('');

  const { markDirty, markClean } = useUnsavedChangeActions();
  const { changeScopeTo } = useScopeChangeAction();
  const scopeId = `github-manual-connect-${organizationId}`;
  const isDirty = installationId.trim().length > 0;

  useEffect(() => {
    if (isDirty) {
      markDirty(scopeId);
    } else {
      markClean(scopeId);
    }
    return () => {
      markClean(scopeId);
    };
  }, [isDirty, markDirty, markClean, scopeId]);

  const handleSubmit = (): void => {
    const trimmed = installationId.trim();
    if (trimmed) {
      onConfirm(trimmed);
    }
  };

  const resetFormState = (): void => {
    setInstallationId('');
    markClean(scopeId);
  };

  const handleClose = (): void => {
    if (isLoading) return;
    if (!isDirty) {
      onClose();
      return;
    }
    changeScopeTo({
      from: scopeId,
      id: 'close-github-manual-connect',
      title: 'Unsaved changes',
      body: 'You have unsaved changes that will be lost.',
      cancelLabel: 'Keep Editing',
      discardLabel: 'Discard',
      run: () => {
        resetFormState();
        onClose();
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Connect {provider.displayName} with Installation ID
          </DialogTitle>
          <DialogDescription>
            Enter the {provider.displayName} App installation ID to connect your
            organization. You can find this in your {provider.displayName} App
            settings or in the URL after installing the app.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="installation-id">Installation ID</Label>
          <Input
            id="installation-id"
            placeholder="e.g. 12345678"
            value={installationId}
            onChange={e => setInstallationId(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && installationId.trim()) {
                handleSubmit();
              }
            }}
            disabled={isLoading}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !installationId.trim() || readOnly}
          >
            {isLoading ? 'Connecting...' : 'Connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
